import React, { useState, useEffect } from 'react';
import { Sparkles, Moon, Sun, User } from 'lucide-react';
import CelestialBackground from './CelestialBackground';

interface SplashPageProps {
  onEnter: () => void;
  onSignIn: () => void;
}

const SplashPage: React.FC<SplashPageProps> = ({ onEnter, onSignIn }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleEnter = () => {
    setIsExiting(true);
    setTimeout(onEnter, 1000);
  };

  return (
    <div 
      className={`fixed inset-0 z-[200] flex items-center justify-center bg-slate-950 transition-all duration-1000 ease-in-out
        ${isExiting ? 'opacity-0 scale-110 pointer-events-none' : 'opacity-100 scale-100'}
      `}
    >
      <CelestialBackground />
      
      <div className="relative z-10 text-center px-4 max-w-lg flex flex-col items-center">
        {/* Mystical Emblem */}
        <div className="mb-12 relative">
          <div className="absolute inset-0 blur-3xl bg-amber-500/10 rounded-full animate-pulse" />
          <div className="relative flex justify-center">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-amber-500/20 flex items-center justify-center animate-[spin_60s_linear_infinite]">
              {/* Internal SVG Pattern representing a simplified zodiac wheel */}
              <svg viewBox="0 0 100 100" className="w-full h-full p-2 text-amber-500/40">
                <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 4" />
                <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="0.5" />
                {[...Array(12)].map((_, i) => (
                  <line 
                    key={i}
                    x1="50" y1="15" x2="50" y2="5" 
                    stroke="currentColor" strokeWidth="0.5" 
                    transform={`rotate(${i * 30}, 50, 50)`} 
                  />
                ))}
              </svg>
            </div>
            
            {/* Centered Icons */}
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-amber-400">
               <Moon size={24} className="animate-pulse" />
               <div className="w-px h-8 bg-amber-500/30" />
               <Sun size={24} className="animate-pulse" style={{ animationDelay: '0.5s' }} />
            </div>
          </div>
        </div>

        {/* Text Reveal */}
        <div className="space-y-6 animate-fade-in">
          <p className="text-amber-500/60 text-[10px] uppercase tracking-[0.5em] font-bold">
            Allie's Celestial Consultancy
          </p>
          
          <h1 className="text-3xl md:text-5xl font-serif text-white tracking-widest uppercase shimmer-text leading-tight">
            Consult the Heavens
          </h1>
          
          <div className="h-px w-16 bg-amber-500/40 mx-auto" />
          
          <p className="text-indigo-200/50 text-xs md:text-sm font-light italic leading-relaxed tracking-wide">
            "As above, so below. As within, so without. Your journey through the cosmic geometry begins here."
          </p>
        </div>

        {/* Action Buttons */}
        <div className="mt-16 flex flex-col items-center gap-6">
          <button 
            onClick={handleEnter}
            className="group relative px-12 py-5 bg-transparent text-white font-bold uppercase tracking-[0.3em] text-[10px] overflow-hidden transition-all"
          >
            {/* Animated Border */}
            <div className="absolute inset-0 border border-amber-500/30 group-hover:border-amber-500 transition-all duration-700" />
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-amber-400" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-amber-400" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-amber-400" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-amber-400" />
            
            {/* Background Glow on Hover */}
            <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/10 transition-all duration-700" />
            
            <span className="relative z-10 flex items-center gap-3">
              Begin Your Alignment <Sparkles size={14} className="group-hover:rotate-12 transition-transform" />
            </span>
          </button>

          <button 
            onClick={onSignIn}
            className="group flex items-center gap-2 text-indigo-400/60 hover:text-amber-400 transition-all text-[9px] uppercase tracking-[0.4em] font-bold"
          >
            <User size={12} className="opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all" /> 
            Already Aligned? Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplashPage;